import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Box, Icon } from '@chakra-ui/react'
import { MdOutlineExpandMore } from 'react-icons/md'
import Select from 'react-select'
import { useAppContext } from 'hooks/useAppContext'

export const getSelectStylesV2 = {
  control: (provided, state) => {
    const { isDisabled, isFocused } = state
    return {
      ...provided,
      minHeight: '32px',
      borderRadius: '3px',
      backgroundColor: isDisabled ? '#EDF2F7' : 'white',
      fontSize: '14px',
      paddingLeft: 0,
      fontFamily: 'inherit',
      color: '#171923',
      borderColor: '#CBD5E0',
      boxShadow: isFocused ? '0 0 0 1px #718096' : 'none',
      '&:hover': { borderColor: '#CBD5E0' }
    }
  },
  valueContainer: (provided) => {
    return {
      ...provided,
      paddingLeft: '10px'
    }
  },
  inputContainer: (provided) => {
    return {
      ...provided
    }
  },
  menu: (provided) => {
    return { ...provided, backgroundColor: 'white', fontSize: '14px', zIndex: 3 }
  },
  indicatorSeparator: (provided) => {
    return { ...provided, display: 'none' }
  },
  indicatorsContainer: (provided) => {
    // was not able to figure out how to specify hover state yet but the color should get darker on hover
    return { ...provided, padding: '8px', height: '32px', color: 'gray.400' }
  }
}

export const SelectTypeaheadStylesV2 = {
  styles: getSelectStylesV2,
  components: {
    DropdownIndicator: () => (
      <Icon
        as={MdOutlineExpandMore}
        color="atomicGray.900"
        fontSize="20px"
      />
    )
  }
}

export const UiSelect = ({
  value,
  loadData,
  apiRespProp = 'rows',
  rootProps,
  inputRef,
  ...rest
}) => {
  const dataTestId = rest['data-testid']
  const classNamePrefix = 'UiSelect'
  const { createToast } = useAppContext()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState(value ? [value] : [])
  const [isLoading, setIsLoading] = useState(false)

  const onSearch = (q) => {
    setSearch(q)
  }

  useEffect(() => {
    setPage(1)
  }, [search])

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await loadData(page, search)
        const data = response?.[apiRespProp] || []
        const filteredData = data.filter(v => value ? v.id !== value.id : true)
        if (page === 1) {
          const valueExistInData = data.filter(v => value ? v.id === value.id : false).length > 0
          setOptions(value && valueExistInData ? [value, ...filteredData] : filteredData)
        } else {
          setOptions([...options, ...data])
        }
        setIsLoading(false)
      } catch (err) {
        createToast('error', 'Error', err.response.data)
        setIsLoading(false)
      }
    }
    load()
  }, [page, search, loadData])

  const handleScroll = (event) => {
    const target = event.target
    if (target.scrollHeight - target.scrollTop === target.clientHeight) {
      setPage(page + 1)
    }
  }

  return (
    <Box
      data-testid={dataTestId}
      {...rootProps}
    >
      <Select
        hideSelectedOptions
        escapeClearsValue
        isClearable
        classNamePrefix={classNamePrefix}
        ref={inputRef}
        onMenuScrollToBottom={handleScroll}
        value={value}
        options={options}
        onInputChange={onSearch}
        isLoading={isLoading}
        {...SelectTypeaheadStylesV2}
        {...rest}
      />
    </Box>
  )
}

UiSelect.propTypes = {
  value: PropTypes.any,
  loadData: PropTypes.func,
  apiRespProp: PropTypes.string,
  rootProps: PropTypes.object,
  inputRef: PropTypes.any
}
